// src/pages/SubscriptionPage.jsx
// Production-level Subscription & Billing Page
// TrueChattr — keyword-based WhatsApp automation (NOT AI)
// Fixed: all features available on all plans (only message limits differ)

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, Zap, Shield, Star, Sparkles,
  Crown, Rocket, Users, MessageSquare, Package,
  HelpCircle, BarChart3, ArrowRight, X,
  CreditCard, Lock, RefreshCw, AlertCircle,
  ChevronRight, ChevronDown, Flame, Award,
  Loader2, Download, Receipt,
  Infinity as InfinityIcon, Headphones, Globe, Layers,
  Calendar, DollarSign, XCircle, Wifi, Bot,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch }                   from '@/components/ui/switch';
import api                          from '@/lib/api';
import { toast }                    from 'sonner';
import { useNavigate }              from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION HELPER
// ─────────────────────────────────────────────────────────────────────────────

const fadeUp = (delay = 0) => ({
  initial:    { opacity: 0, y: 24 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay },
});

// ─────────────────────────────────────────────────────────────────────────────
// RAZORPAY HELPER
// ─────────────────────────────────────────────────────────────────────────────

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script   = document.createElement('script');
    script.src     = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const openRazorpayCheckout = ({ order, key, userName, userEmail, description, onSuccess, onFailure }) => {
  const options = {
    key,
    amount:      order.amount,
    currency:    order.currency,
    name:        'TrueChattr',
    description,
    order_id:    order.id,
    prefill:     { name: userName || '', email: userEmail || '' },
    theme:       { color: '#059669' },
    modal:       { ondismiss: () => onFailure('Payment cancelled by user') },
    handler:     (response) => onSuccess(response),
  };
  const rzp = new window.Razorpay(options);
  rzp.on('payment.failed', (resp) => onFailure(resp.error?.description || 'Payment failed'));
  rzp.open();
};

// ─────────────────────────────────────────────────────────────────────────────
// PLAN DATA
// IMPORTANT: Sabhi plans mein sab features ✅ hain — sirf message limit alag hai
// "AI bot" → "keyword bot" (TrueChattr keyword-based automation hai)
// ─────────────────────────────────────────────────────────────────────────────

const PLANS = [
  {
    id:           'starter',
    name:         'Starter',
    icon:         Zap,
    iconColor:    'text-blue-500',
    iconBg:       'bg-blue-50',
    monthlyPrice: 0,
    yearlyPrice:  0,
    onetimePrice: null,
    description:  'Perfect for testing the waters',
    badge:        null,
    popular:      false,
    lifetime:     false,
    color:        'border-gray-200 hover:border-blue-300',
    btnVariant:   'outline',
    highlight:    'Up to 200 messages/month',
    features: [
      { text: '200 messages / month',          icon: MessageSquare, included: true },
      { text: '1 WhatsApp number',             icon: Wifi,          included: true },
      { text: 'Keyword-based auto-reply bot',  icon: Bot,           included: true },
      { text: 'Unlimited keywords',            icon: Zap,           included: true },
      { text: 'Unlimited products & FAQs',     icon: Package,       included: true },
      { text: 'Analytics dashboard',           icon: BarChart3,     included: true },
      { text: 'Lead capture',                  icon: Users,         included: true },
      { text: 'Bulk broadcast',                icon: MessageSquare, included: true },
      { text: 'Email support',                 icon: Headphones,    included: true },
      { text: 'Multi-agent support',           icon: Users,         included: false },
    ],
  },
  {
    id:           'basic',
    name:         'Basic',
    icon:         Shield,
    iconColor:    'text-teal-600',
    iconBg:       'bg-teal-50',
    monthlyPrice: 19,
    yearlyPrice:  12,
    onetimePrice: null,
    description:  'For small businesses getting started',
    badge:        null,
    popular:      false,
    lifetime:     false,
    color:        'border-teal-200 hover:border-teal-400',
    btnVariant:   'outline',
    highlight:    'Up to 2,500 messages/month',
    features: [
      { text: '2,500 messages / month',        icon: MessageSquare, included: true },
      { text: '1 WhatsApp number',             icon: Wifi,          included: true },
      { text: 'Keyword-based auto-reply bot',  icon: Bot,           included: true },
      { text: 'Unlimited keywords',            icon: Zap,           included: true },
      { text: 'Unlimited products & FAQs',     icon: Package,       included: true },
      { text: 'Analytics dashboard',           icon: BarChart3,     included: true },
      { text: 'Lead capture',                  icon: Users,         included: true },
      { text: 'Bulk broadcast',                icon: MessageSquare, included: true },
      { text: 'Email support',                 icon: Headphones,    included: true },
      { text: 'Multi-agent support',           icon: Users,         included: false },
    ],
  },
  {
    id:           'pro',
    name:         'Pro',
    icon:         Star,
    iconColor:    'text-emerald-600',
    iconBg:       'bg-emerald-50',
    monthlyPrice: 49,
    yearlyPrice:  29,
    onetimePrice: null,
    description:  'Best for growing businesses',
    badge:        'Most Popular',
    popular:      true,
    lifetime:     false,
    color:        'border-emerald-500',
    btnVariant:   'primary',
    highlight:    'Up to 7,500 messages/month',
    features: [
      { text: '7,500 messages / month',        icon: MessageSquare, included: true },
      { text: '1 WhatsApp number',             icon: Wifi,          included: true },
      { text: 'Keyword-based auto-reply bot',  icon: Bot,           included: true },
      { text: 'Unlimited keywords',            icon: Zap,           included: true },
      { text: 'Unlimited products & FAQs',     icon: Package,       included: true },
      { text: 'Analytics dashboard',           icon: BarChart3,     included: true },
      { text: 'Lead capture',                  icon: Users,         included: true },
      { text: 'Bulk broadcast',                icon: MessageSquare, included: true },
      { text: 'Priority support',              icon: Headphones,    included: true },
      { text: 'Multi-agent support',           icon: Users,         included: false },
    ],
  },
  {
    id:           'business',
    name:         'Business',
    icon:         Crown,
    iconColor:    'text-violet-600',
    iconBg:       'bg-violet-50',
    monthlyPrice: 149,
    yearlyPrice:  99,
    onetimePrice: null,
    description:  'For established companies',
    badge:        'Best Value',
    popular:      false,
    lifetime:     false,
    color:        'border-violet-300 hover:border-violet-500',
    btnVariant:   'secondary',
    highlight:    'Up to 25,000 messages/month',
    features: [
      { text: '25,000 messages / month',       icon: MessageSquare, included: true },
      { text: '3 WhatsApp numbers',            icon: Wifi,          included: true },
      { text: 'Keyword-based auto-reply bot',  icon: Bot,           included: true },
      { text: 'Unlimited keywords',            icon: Zap,           included: true },
      { text: 'Unlimited products & FAQs',     icon: Package,       included: true },
      { text: 'Analytics dashboard',           icon: BarChart3,     included: true },
      { text: 'Lead capture',                  icon: Users,         included: true },
      { text: 'Bulk broadcast',                icon: MessageSquare, included: true },
      { text: 'Multi-agent support',           icon: Users,         included: true },
      { text: 'Dedicated support',             icon: Headphones,    included: true },
    ],
  },
  {
    id:           'lifetime',
    name:         'Lifetime',
    icon:         InfinityIcon,
    iconColor:    'text-orange-600',
    iconBg:       'bg-orange-50',
    monthlyPrice: null,
    yearlyPrice:  null,
    onetimePrice: 499,
    description:  'Pay once, keep forever',
    badge:        '🔥 Limited Deal',
    popular:      false,
    lifetime:     true,
    color:        'border-orange-300 hover:border-orange-500',
    btnVariant:   'outline',
    highlight:    '10,000 messages/month — forever',
    features: [
      { text: '10,000 messages / month',       icon: MessageSquare, included: true },
      { text: '1 WhatsApp number',             icon: Wifi,          included: true },
      { text: 'Keyword-based auto-reply bot',  icon: Bot,           included: true },
      { text: 'Unlimited keywords',            icon: Zap,           included: true },
      { text: 'Unlimited products & FAQs',     icon: Package,       included: true },
      { text: 'Analytics dashboard',           icon: BarChart3,     included: true },
      { text: 'Lead capture',                  icon: Users,         included: true },
      { text: 'Bulk broadcast',                icon: MessageSquare, included: true },
      { text: 'Priority support',              icon: Headphones,    included: true },
      { text: 'No monthly fees — ever',        icon: InfinityIcon,  included: true },
    ],
  },
  {
    id:           'enterprise',
    name:         'Enterprise',
    icon:         Rocket,
    iconColor:    'text-purple-600',
    iconBg:       'bg-purple-50',
    monthlyPrice: null,
    yearlyPrice:  null,
    onetimePrice: null,
    description:  'Tailored for large organizations',
    badge:        'Custom',
    popular:      false,
    lifetime:     false,
    color:        'border-purple-300 hover:border-purple-500',
    btnVariant:   'outline',
    highlight:    'Unlimited messages — custom setup',
    features: [
      { text: 'Unlimited messages',            icon: MessageSquare, included: true },
      { text: 'Unlimited WA numbers',          icon: Wifi,          included: true },
      { text: 'Keyword-based auto-reply bot',  icon: Bot,           included: true },
      { text: 'Unlimited keywords',            icon: Zap,           included: true },
      { text: 'Unlimited products & FAQs',     icon: Package,       included: true },
      { text: 'Analytics dashboard',           icon: BarChart3,     included: true },
      { text: 'Lead capture',                  icon: Users,         included: true },
      { text: 'Multi-agent support',           icon: Users,         included: true },
      { text: 'White label options',           icon: Award,         included: true },
      { text: 'Dedicated account manager',     icon: Headphones,    included: true },
    ],
  },
];

// Add-ons — "AI Custom Training" removed (we don't have AI/ML)
const ADD_ONS = [
  { id: 'extra_messages',   name: 'Extra Messages',       price: 9,  unit: '/1,000 msgs', icon: MessageSquare, desc: 'Add more messages to your current plan'   },
  { id: 'extra_number',     name: 'Extra WA Number',      price: 19, unit: '/month',       icon: Wifi,          desc: 'Add another WhatsApp number to your account' },
  { id: 'extra_keywords',   name: 'Keyword Packs',        price: 0,  unit: 'Included',     icon: Zap,           desc: 'Unlimited keywords already included in all plans' },
  { id: 'priority_support', name: 'Priority Support',     price: 29, unit: '/month',       icon: Headphones,    desc: '24/7 dedicated support agent on call'     },
];

const FAQS = [
  { q: 'Can I change my plan anytime?',
    a: 'Yes! You can upgrade or downgrade anytime. Changes take effect immediately and billing is prorated.' },
  { q: 'What payment methods do you accept?',
    a: 'We accept all major credit/debit cards (Visa, Mastercard, Amex), UPI, net banking, and wallets — all secured via Razorpay.' },
  { q: 'Is there a free plan?',
    a: 'Yes! Our Starter plan is completely free forever — no credit card required. You get 200 messages/month and access to all features.' },
  { q: 'Do all plans include all features?',
    a: 'Yes! Every plan — including Starter — includes all core features: auto-reply bot, analytics, lead capture, bulk broadcast, products, FAQs, and keywords. The only difference between plans is the monthly message limit.' },
  { q: 'What happens if I exceed my message limit?',
    a: "We'll send an email alert at 80% usage. You can upgrade or purchase extra message packs to avoid any interruption." },
  { q: 'Can I cancel anytime?',
    a: 'Absolutely. Cancel anytime from billing settings. You keep access until end of your billing period — no cancellation fees.' },
  { q: 'Do you offer refunds?',
    a: "Yes — 7-day money-back guarantee on all paid plans. Contact support and we'll process your refund within 24 hours." },
  { q: 'What is the Lifetime Deal?',
    a: 'One-time payment of $499 that gives you 10,000 messages/month and all features forever. No monthly fees, ever. Limited to 50 spots only.' },
];

// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE BUTTON
// ─────────────────────────────────────────────────────────────────────────────

const Btn = ({ children, variant = 'primary', className = '', loading = false, ...props }) => {
  const base = 'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed';
  const styles = {
    primary:   'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25',
    secondary: 'bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-600/25',
    orange:    'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/25',
    outline:   'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-emerald-400 hover:text-emerald-600',
    ghost:     'bg-white/10 hover:bg-white/20 text-white border border-white/20',
    danger:    'bg-red-50 hover:bg-red-100 text-red-600 border border-red-200',
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} disabled={loading || props.disabled} {...props}>
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CURRENT PLAN BANNER
// ─────────────────────────────────────────────────────────────────────────────

function CurrentPlanBanner({ subscription, onManage }) {
  const plan       = PLANS.find(p => p.id === subscription?.plan_id) || PLANS[0];
  const Icon       = plan.icon;
  const isLifetime = subscription?.plan_id === 'lifetime';
  const daysLeft   = subscription?.ends_at
    ? Math.ceil((new Date(subscription.ends_at) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <motion.div {...fadeUp(0.04)} className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 p-6 shadow-xl shadow-emerald-200/40">
      <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl pointer-events-none" />
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />

      <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
        {/* Left */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
            <Icon className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-emerald-100 text-xs font-medium mb-0.5">Current Plan</p>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              {plan.name}
              {isLifetime && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-400/30 border border-orange-300/50 text-orange-200 font-bold">
                  ♾ Lifetime
                </span>
              )}
            </h2>
            <p className="text-emerald-100 text-sm mt-0.5">{plan.highlight}</p>
          </div>
        </div>

        {/* Center stats */}
        <div className="flex gap-6">
          {[
            { label: 'Status',  value: subscription?.status === 'active' ? '✅ Active' : '⚠️ Inactive' },
            {
              label: 'Billing',
              value: isLifetime
                ? '♾ One-time'
                : subscription?.billing_cycle === 'yearly' ? '📅 Yearly' : '📅 Monthly',
            },
            {
              label: 'Renewal',
              value: isLifetime
                ? '∞ Never Expires'
                : subscription?.ends_at
                  ? new Date(subscription.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '∞ Free',
            },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-emerald-100 text-xs mb-0.5">{label}</p>
              <p className="text-white font-semibold text-sm">{value}</p>
            </div>
          ))}
        </div>

        {/* Right */}
        <div className="flex items-center gap-3">
          {daysLeft !== null && daysLeft <= 7 && !isLifetime && (
            <div className="px-3 py-1.5 rounded-full bg-yellow-400/20 border border-yellow-300/30 text-yellow-200 text-xs font-semibold">
              ⏳ {daysLeft} days left
            </div>
          )}
          <Btn variant="ghost" className="px-5 py-2.5 text-sm" onClick={onManage}>
            Manage Plan <ChevronRight className="w-4 h-4" />
          </Btn>
        </div>
      </div>

      {/* Usage bar */}
      {subscription?.messages_used !== undefined && (
        <div className="relative mt-5 pt-5 border-t border-white/20">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-emerald-100">Messages used this month</span>
            <span className="text-white font-bold">
              {subscription.messages_used?.toLocaleString()} / {subscription.messages_limit?.toLocaleString()}
            </span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-white rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${Math.min((subscription.messages_used / subscription.messages_limit) * 100, 100)}%` }}
              transition={{ duration: 1.2, delay: 0.3 }}
            />
          </div>
          <p className="text-emerald-100 text-xs mt-1.5">
            {Math.round((subscription.messages_used / subscription.messages_limit) * 100)}% used
            {subscription.messages_used / subscription.messages_limit > 0.8 && (
              <span className="ml-2 text-yellow-300 font-semibold">⚠️ Consider upgrading soon</span>
            )}
          </p>
        </div>
      )}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BILLING TOGGLE
// ─────────────────────────────────────────────────────────────────────────────

function BillingToggle({ yearly, onChange }) {
  return (
    <div className="flex items-center justify-center gap-4">
      <span className={`text-sm font-semibold ${!yearly ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>Monthly</span>
      <Switch checked={yearly} onCheckedChange={onChange} />
      <span className={`text-sm font-semibold ${yearly ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
        Yearly
        <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">Save 40%</span>
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PLAN CARD
// ─────────────────────────────────────────────────────────────────────────────

function PlanCard({ plan, yearly, currentPlanId, onSelect, loading, lifetimeSpotsLeft, hasPaidBefore }) {
  const Icon      = plan.icon;
  const isCurrent = plan.id === currentPlanId;
  const planIdx   = PLANS.findIndex(p => p.id === plan.id);
  const currIdx   = PLANS.findIndex(p => p.id === currentPlanId);
  const isUpgrade = planIdx > currIdx;

  return (
    <motion.div
      {...fadeUp(0.08 + planIdx * 0.06)}
      className={`relative rounded-2xl border-2 p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col
        ${plan.popular
          ? 'bg-gradient-to-br from-emerald-600 to-emerald-500 border-emerald-600 shadow-xl shadow-emerald-200/40'
          : plan.lifetime
            ? 'bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/10 dark:to-yellow-900/10 ' + plan.color
            : 'bg-white dark:bg-gray-800 ' + plan.color
        }
        ${isCurrent ? 'ring-2 ring-offset-2 ring-emerald-500' : ''}
      `}
    >
      {/* Badge */}
      {plan.badge && (
        <div className={`absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap
          ${plan.popular         ? 'bg-yellow-400 text-yellow-900'
          : plan.lifetime        ? 'bg-orange-400 text-white'
          : plan.id === 'business' ? 'bg-violet-100 text-violet-700'
          : 'bg-purple-100 text-purple-700'}
        `}>
          {plan.badge}
        </div>
      )}

      {isCurrent && (
        <div className="absolute -top-3.5 right-4 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold border border-emerald-200">
          ✓ Current
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${plan.popular ? 'bg-white/20' : plan.iconBg}`}>
          <Icon className={`w-5 h-5 ${plan.popular ? 'text-white' : plan.iconColor}`} />
        </div>
        <div>
          <h3 className={`text-lg font-bold ${plan.popular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{plan.name}</h3>
          <p className={`text-xs ${plan.popular ? 'text-emerald-100' : 'text-gray-500 dark:text-gray-400'}`}>{plan.description}</p>
        </div>
      </div>

      {/* Price */}
      <div className="mb-5">
        {plan.lifetime ? (
          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-orange-600">${plan.onetimePrice}</span>
              <span className="text-sm text-gray-400 line-through">$1,176</span>
            </div>
            <p className="text-xs mt-1 font-semibold text-orange-600">One-time payment • Save $677</p>
            {lifetimeSpotsLeft !== null
              ? <p className="text-xs mt-1 text-red-500 font-bold">🔥 Only {lifetimeSpotsLeft} spots left!</p>
              : <p className="text-xs mt-1 text-orange-500 font-bold">🔥 Limited spots available!</p>
            }
          </div>
        ) : plan.monthlyPrice === null ? (
          <div>
            <span className="text-4xl font-bold text-gray-900 dark:text-white">Custom</span>
            <p className="text-xs mt-1 text-gray-400">Contact us for pricing</p>
          </div>
        ) : plan.monthlyPrice === 0 ? (
          <div>
            <span className="text-4xl font-bold text-gray-900 dark:text-white">Free</span>
            <p className="text-xs mt-1 text-gray-400">Forever, no credit card</p>
          </div>
        ) : (
          <div>
            <span className={`text-sm line-through ${plan.popular ? 'text-emerald-200' : 'text-gray-400'}`}>
              ${plan.monthlyPrice}/mo
            </span>
            <div className="flex items-baseline gap-1">
              <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                ${yearly ? plan.yearlyPrice : (!hasPaidBefore ? Math.round(plan.monthlyPrice * 0.5) : plan.monthlyPrice)}
              </span>
              <span className={`text-sm ${plan.popular ? 'text-emerald-100' : 'text-gray-400'}`}>/mo</span>
            </div>
            {yearly ? (
              <p className={`text-xs mt-1 font-semibold ${plan.popular ? 'text-yellow-300' : 'text-emerald-600'}`}>
                Billed ${plan.yearlyPrice * 12}/year — Save ${(plan.monthlyPrice - plan.yearlyPrice) * 12}!
              </p>
            ) : (
              <p className={`text-xs mt-1 font-semibold ${plan.popular ? 'text-yellow-300' : 'text-orange-500'}`}>
                🎁 First 3 months only!
              </p>
            )}
          </div>
        )}
      </div>

      {/* Message limit highlight */}
      <div className={`mb-4 px-3 py-2 rounded-xl text-xs font-semibold text-center
        ${plan.popular ? 'bg-white/15 text-white' : plan.lifetime ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'}`}>
        📨 {plan.highlight}
      </div>

      {/* Features */}
      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.features.map((feat, i) => {
          return (
            <li key={i} className="flex items-center gap-2.5">
              {feat.included
                ? <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${plan.popular ? 'text-white' : plan.lifetime ? 'text-orange-500' : 'text-emerald-500'}`} />
                : <XCircle      className={`w-4 h-4 flex-shrink-0 ${plan.popular ? 'text-emerald-300/50' : 'text-gray-300'}`} />
              }
              <span className={`text-sm ${
                !feat.included
                  ? plan.popular ? 'text-emerald-200/50 line-through' : 'text-gray-300 dark:text-gray-600 line-through'
                  : plan.popular  ? 'text-emerald-50'
                  : plan.lifetime ? 'text-orange-900 dark:text-orange-100'
                  : 'text-gray-700 dark:text-gray-300'
              }`}>
                {feat.text}
              </span>
            </li>
          );
        })}
      </ul>

      {/* CTA */}
      {isCurrent ? (
        <div className={`w-full py-2.5 rounded-xl text-center text-sm font-semibold
          ${plan.popular ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
          ✓ Current Plan
        </div>
      ) : plan.id === 'enterprise' ? (
        <Btn variant={plan.popular ? 'ghost' : 'outline'} className="w-full py-2.5 text-sm" onClick={() => onSelect(plan)}>
          Contact Sales <ArrowRight className="w-4 h-4" />
        </Btn>
      ) : plan.lifetime ? (
        <Btn
          variant="orange"
          className="w-full py-2.5 text-sm"
          loading={loading === plan.id}
          disabled={lifetimeSpotsLeft === 0}
          onClick={() => onSelect(plan)}
        >
          {lifetimeSpotsLeft === 0 ? 'Sold Out' : '🔥 Grab Lifetime Deal'} <ArrowRight className="w-4 h-4" />
        </Btn>
      ) : (
        <Btn
          variant={plan.popular ? 'ghost' : plan.btnVariant}
          className={`w-full py-2.5 text-sm ${plan.id === 'business' ? 'bg-violet-600 hover:bg-violet-700 text-white border-0' : ''}`}
          loading={loading === plan.id}
          onClick={() => onSelect(plan)}
        >
          {isUpgrade ? '⬆️ Upgrade' : '⬇️ Downgrade'} to {plan.name}
          <ArrowRight className="w-4 h-4" />
        </Btn>
      )}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ADD-ON CARD
// ─────────────────────────────────────────────────────────────────────────────

function AddOnCard({ addon, active, onToggle }) {
  const Icon      = addon.icon;
  const isFree    = addon.price === 0;
  const isToggleable = !isFree;

  return (
    <div
      className={`rounded-xl border-2 p-4 transition-all duration-200
        ${isFree ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/10 cursor-default'
        : active  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10 cursor-pointer'
        : 'border-gray-200 dark:border-gray-700 hover:border-emerald-300 cursor-pointer'}`}
      onClick={() => isToggleable && onToggle(addon.id)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center
            ${isFree ? 'bg-emerald-100 dark:bg-emerald-900/30' : active ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
            <Icon className={`w-4 h-4 ${isFree ? 'text-emerald-600' : active ? 'text-emerald-600' : 'text-gray-500'}`} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              {addon.name}
              {isFree && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Included Free</span>}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{addon.desc}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            {isFree
              ? <p className="text-sm font-bold text-emerald-600">✓ Free</p>
              : <>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">${addon.price}</p>
                  <p className="text-xs text-gray-400">{addon.unit}</p>
                </>
            }
          </div>
          {isToggleable && (
            <Switch checked={active} onCheckedChange={() => onToggle(addon.id)} onClick={e => e.stopPropagation()} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INVOICE ROW
// ─────────────────────────────────────────────────────────────────────────────

function InvoiceRow({ invoice }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
          ${invoice.status === 'paid' ? 'bg-emerald-50' : 'bg-red-50'}`}>
          <Receipt className={`w-4 h-4 ${invoice.status === 'paid' ? 'text-emerald-600' : 'text-red-500'}`} />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{invoice.description}</p>
          <p className="text-xs text-gray-400">
            {new Date(invoice.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold
          ${invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
          {invoice.status === 'paid' ? '✓ Paid' : '✗ Failed'}
        </span>
        <span className="text-sm font-bold text-gray-900 dark:text-white">₹{invoice.amount}</span>
        <button className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <Download className="w-3.5 h-3.5 text-gray-400" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FAQ ACCORDION
// ─────────────────────────────────────────────────────────────────────────────

function FaqItem({ faq, isOpen, onToggle }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-center justify-between font-semibold text-sm text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
      >
        {faq.q}
        <ChevronDown className={`w-4 h-4 flex-shrink-0 ml-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{faq.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIRM MODAL
// ─────────────────────────────────────────────────────────────────────────────

function ConfirmModal({ plan, yearly, addons, onConfirm, onClose, loading, hasPaidBefore }) {
  if (!plan) return null;

  const isLifetime = plan.lifetime;
  const basePrice  = isLifetime
    ? plan.onetimePrice
    : yearly
      ? plan.yearlyPrice
      : (!hasPaidBefore && plan.monthlyPrice > 0
          ? Math.round(plan.monthlyPrice * 0.5)
          : plan.monthlyPrice);

  const activeAddonsData = addons
    .map(id => ADD_ONS.find(a => a.id === id && a.price > 0))
    .filter(Boolean);

  const addonTotal = activeAddonsData.reduce((s, a) => s + (a?.price || 0), 0);
  const total      = (basePrice || 0) + (isLifetime ? 0 : addonTotal);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-8 border border-gray-200 dark:border-gray-700"
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <X className="w-5 h-5 text-gray-500" />
        </button>

        <div className="text-center mb-6">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4
            ${isLifetime ? 'bg-orange-100' : 'bg-emerald-100 dark:bg-emerald-900/30'}`}>
            {isLifetime
              ? <InfinityIcon className="w-8 h-8 text-orange-600" />
              : <CreditCard   className="w-8 h-8 text-emerald-600" />
            }
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            {isLifetime ? '🔥 Confirm Lifetime Deal' : 'Confirm Subscription'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Review your order before proceeding</p>
        </div>

        {/* Order summary */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-6 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              {plan.name} Plan ({isLifetime ? 'One-time Payment' : yearly ? 'Yearly' : 'Monthly'})
            </span>
            <span className="font-semibold text-gray-900 dark:text-white">
              ${basePrice}{isLifetime ? '' : '/mo'}
            </span>
          </div>

          {!isLifetime && activeAddonsData.map(a => (
            <div key={a.id} className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">{a.name}</span>
              <span className="font-semibold text-gray-900 dark:text-white">${a.price}{a.unit}</span>
            </div>
          ))}

          <div className="border-t border-gray-200 dark:border-gray-700 pt-3 flex justify-between">
            <span className="font-bold text-gray-900 dark:text-white">Total</span>
            <span className={`font-bold text-lg ${isLifetime ? 'text-orange-600' : 'text-emerald-600'}`}>
              ${total}{isLifetime ? '' : '/mo'}
            </span>
          </div>

          {isLifetime && (
            <p className="text-xs text-center text-orange-600 font-semibold">
              💰 You save $677 vs monthly plan! Pay once, use forever.
            </p>
          )}
          {!isLifetime && yearly && plan.monthlyPrice > 0 && (
            <p className="text-xs text-center text-emerald-600 font-semibold">
              💰 You save ${(plan.monthlyPrice - plan.yearlyPrice) * 12} per year!
            </p>
          )}
          {!isLifetime && !yearly && !hasPaidBefore && plan.monthlyPrice > 0 && (
            <p className="text-xs text-center text-orange-500 font-semibold">
              🎁 Launch offer: 50% off for first 3 months, then ${plan.monthlyPrice}/mo
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 mb-5 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
          <Lock className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <p className="text-xs text-blue-700 dark:text-blue-300">Secured by 256-bit SSL. 7-day money-back guarantee.</p>
        </div>

        <div className="flex gap-3">
          <Btn variant="outline" className="flex-1 py-3" onClick={onClose}>Cancel</Btn>
          <Btn
            variant={isLifetime ? 'orange' : 'primary'}
            className="flex-1 py-3"
            loading={loading}
            onClick={onConfirm}
          >
            {isLifetime ? '🔥 Grab Deal' : 'Confirm & Pay'}
          </Btn>
        </div>
      </motion.div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CANCEL MODAL
// ─────────────────────────────────────────────────────────────────────────────

function CancelModal({ onConfirm, onClose, loading }) {
  const [reason, setReason] = useState('');
  const reasons = ['Too expensive', 'Not using it enough', 'Missing features', 'Switching to competitor', 'Technical issues', 'Other'];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-8 border border-gray-200 dark:border-gray-700"
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <X className="w-5 h-5 text-gray-500" />
        </button>
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">😢</div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Cancel Subscription?</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            You'll lose your higher message limit. You can downgrade to the free Starter plan instead.
          </p>
        </div>
        <div className="mb-5">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Why are you canceling? (optional)</p>
          <div className="grid grid-cols-2 gap-2">
            {reasons.map(r => (
              <button
                key={r}
                onClick={() => setReason(r)}
                className={`text-xs px-3 py-2 rounded-lg border transition-all
                  ${reason === r ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 mb-5">
          <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">⚠️ Before you go, consider:</p>
          <ul className="text-xs text-amber-600 dark:text-amber-400 mt-2 space-y-1">
            <li>• Downgrade to Starter (free) — keep all features, 200 msgs/month</li>
            <li>• Buy a message top-up pack instead</li>
            <li>• Contact support — we may be able to help</li>
          </ul>
        </div>
        <div className="flex gap-3">
          <Btn variant="primary" className="flex-1 py-3" onClick={onClose}>Keep My Plan</Btn>
          <Btn variant="danger" className="flex-1 py-3" loading={loading} onClick={() => onConfirm(reason)}>
            Cancel Anyway
          </Btn>
        </div>
      </motion.div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SUBSCRIPTION PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function SubscriptionPage() {
  const navigate = useNavigate();

  const [subscription,      setSubscription]      = useState(null);
  const [invoices,          setInvoices]          = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [planLoading,       setPlanLoading]       = useState(null);
  const [cancelLoading,     setCancelLoading]     = useState(false);
  const [error,             setError]             = useState(null);
  const [lifetimeSpotsLeft, setLifetimeSpotsLeft] = useState(null);

  const [yearly,        setYearly]        = useState(false);
  const [activeAddons,  setActiveAddons]  = useState([]);
  const [openFaq,       setOpenFaq]       = useState(null);
  const [confirmPlan,   setConfirmPlan]   = useState(null);
  const [showCancel,    setShowCancel]    = useState(false);
  const [activeTab,     setActiveTab]     = useState('plans');
  const [hasPaidBefore, setHasPaidBefore] = useState(false);

  useEffect(() => {
    fetchData();
    fetchLifetimeSpots();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [subRes, invRes] = await Promise.all([
        api.get('/subscription/current'),
        api.get('/subscription/invoices'),
      ]);
      const hasPaid = (invRes.data.invoices || []).some(i => i.status === 'paid');
      setHasPaidBefore(hasPaid);
      if (subRes.data.success) setSubscription(subRes.data.subscription);
      if (invRes.data.success) setInvoices(invRes.data.invoices || []);
      if (subRes.data.subscription?.addons) setActiveAddons(subRes.data.subscription.addons);
    } catch (err) {
      setError('Failed to load subscription data. Please refresh.');
      toast.error('Could not load billing data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLifetimeSpots = async () => {
    try {
      const res = await api.get('/subscription/lifetime-spots');
      if (res.data.success) setLifetimeSpotsLeft(res.data.spotsLeft);
    } catch {
      setLifetimeSpotsLeft(null);
    }
  };

  const handleSelectPlan = (plan) => {
    if (plan.id === 'enterprise') {
      window.location.href = 'mailto:hello@truechattr.com?subject=Enterprise Plan Inquiry';
      return;
    }
    if (plan.lifetime && lifetimeSpotsLeft === 0) {
      toast.error('Lifetime deal spots are sold out!');
      return;
    }
    setConfirmPlan(plan);
  };

  const handleConfirmSubscription = async () => {
    if (!confirmPlan) return;
    const planToProcess = confirmPlan;
    const isLifetime    = planToProcess.lifetime;
    const isFree        = planToProcess.monthlyPrice === 0;

    try {
      setPlanLoading(confirmPlan.id);

      if (isFree) {
        const res = await api.post('/subscription/subscribe', {
          planId:       confirmPlan.id,
          billingCycle: 'monthly',
          addons:       [],
        });
        if (!res.data.success) throw new Error(res.data.error);
        toast.success(`✅ Switched to ${confirmPlan.name} plan!`);
        setSubscription(res.data.subscription);
        setConfirmPlan(null);
        return;
      }

      const rzpLoaded = await loadRazorpay();
      if (!rzpLoaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        return;
      }

      const orderRes = await api.post('/subscription/create-order', {
        planId:       confirmPlan.id,
        billingCycle: isLifetime ? 'lifetime' : (yearly ? 'yearly' : 'monthly'),
        addons:       isLifetime ? [] : activeAddons.filter(id => ADD_ONS.find(a => a.id === id && a.price > 0)),
      });

      if (!orderRes.data.success) throw new Error(orderRes.data.error);
      const { order, key, isLaunchOffer } = orderRes.data;

      setConfirmPlan(null);

      openRazorpayCheckout({
        order,
        key,
        description: isLifetime
          ? 'TrueChattr Lifetime Deal — One-time'
          : isLaunchOffer
            ? `TrueChattr ${confirmPlan.name} Plan — 🎁 50% Launch Offer`
            : `TrueChattr ${confirmPlan.name} Plan (${yearly ? 'Yearly' : 'Monthly'})`,
        onSuccess: async (response) => {
          try {
            const verifyRes = await api.post('/subscription/verify-payment', {
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              planId:              planToProcess.id,
              billingCycle:        isLifetime ? 'lifetime' : (yearly ? 'yearly' : 'monthly'),
              addons:              isLifetime ? [] : activeAddons.filter(id => ADD_ONS.find(a => a.id === id && a.price > 0)),
            });
            if (!verifyRes.data.success) throw new Error(verifyRes.data.error);
            toast.success(`🎉 ${isLifetime ? 'Lifetime Deal activated!' : `Subscribed to ${planToProcess.name}!`}`);
            setSubscription(verifyRes.data.subscription);
            if (isLifetime) setLifetimeSpotsLeft(prev => Math.max(0, (prev || 1) - 1));
            fetchData();
          } catch {
            toast.error('Payment done but verification failed. Contact support with payment ID: ' + response.razorpay_payment_id);
          }
        },
        onFailure: (msg) => {
          if (msg !== 'Payment cancelled by user') toast.error(msg || 'Payment failed. Please try again.');
        },
      });

    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to process subscription');
    } finally {
      setPlanLoading(null);
    }
  };

  const handleCancelSubscription = async (reason) => {
    try {
      setCancelLoading(true);
      await api.post('/subscription/cancel', { reason });
      toast.success('Subscription canceled. You keep access until end of billing period.');
      setShowCancel(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel. Please contact support.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleToggleAddon = (addonId) => {
    setActiveAddons(prev =>
      prev.includes(addonId) ? prev.filter(a => a !== addonId) : [...prev, addonId]
    );
  };

  const handleSaveAddons = async () => {
    try {
      const paidAddons = activeAddons.filter(id => ADD_ONS.find(a => a.id === id && a.price > 0));
      const res = await api.post('/subscription/update-addons', { addons: paidAddons });
      if (!res.data.success) throw new Error(res.data.error);
      toast.success('Add-ons updated successfully!');
      setSubscription(res.data.subscription);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save add-ons');
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-140px)] flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center gap-5">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-100" />
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
            <div className="absolute inset-3 rounded-full bg-emerald-50 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-700">Loading subscription</p>
            <p className="text-xs text-gray-400 mt-0.5">Fetching your billing details…</p>
          </div>
        </motion.div>
      </div>
    );
  }

  const isCurrentLifetime = subscription?.plan_id === 'lifetime';

  return (
    <div className="space-y-6 px-1 pb-8" data-testid="subscription-page">

      {/* PAGE HEADER */}
      <motion.div {...fadeUp(0)} className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span className="text-xs font-medium text-gray-500">Billing & Subscription</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
            Manage Subscription 💳
          </h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400 text-sm">
            All plans include every feature — upgrade for a higher message limit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all shadow-sm"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          {subscription?.plan_id !== 'starter' && !isCurrentLifetime && (
            <Btn variant="danger" className="px-4 py-2 text-sm" onClick={() => setShowCancel(true)}>
              Cancel Plan
            </Btn>
          )}
        </div>
      </motion.div>

      {/* Error */}
      {error && (
        <motion.div {...fadeUp(0.04)}>
          <Alert variant="destructive" className="border-red-200 bg-red-50 text-red-700">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </motion.div>
      )}

      {/* CURRENT PLAN BANNER */}
      <CurrentPlanBanner subscription={subscription} onManage={() => setActiveTab('billing')} />

      {/* WHAT'S INCLUDED NOTE */}
      <motion.div {...fadeUp(0.1)}>
        <div className="flex items-start gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
              All features included in every plan
            </p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">
              Auto-reply bot, analytics, lead capture, bulk broadcast, products, FAQs, keywords — all available on every plan including free.
              Plans only differ in monthly message limit.
            </p>
          </div>
        </div>
      </motion.div>

      {/* TABS */}
      <motion.div {...fadeUp(0.12)}>
        <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-fit">
          {[
            { id: 'plans',   label: '📦 Plans'   },
            { id: 'addons',  label: '⚡ Add-ons'  },
            { id: 'billing', label: '🧾 Billing'  },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* ═══ TAB CONTENT ═══ */}
      <AnimatePresence mode="wait">

        {/* ── PLANS TAB ── */}
        {activeTab === 'plans' && (
          <motion.div key="plans" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <BillingToggle yearly={yearly} onChange={setYearly} />
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-600 to-emerald-500 text-white text-sm font-semibold shadow-lg">
                <Sparkles className="w-4 h-4" />
                🎁 Early Bird: 50% OFF first 3 months!
              </div>
            </div>

            {/* Plan cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-5">
              {PLANS.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  yearly={yearly}
                  hasPaidBefore={hasPaidBefore}
                  currentPlanId={subscription?.plan_id}
                  onSelect={handleSelectPlan}
                  loading={planLoading}
                  lifetimeSpotsLeft={plan.lifetime ? lifetimeSpotsLeft : null}
                />
              ))}
            </div>

            {/* Feature comparison table — honest: only message limits differ */}
            <motion.div {...fadeUp(0.4)} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
              <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-emerald-500" /> Plan Comparison
              </h2>
              <p className="text-xs text-gray-400 mb-5">All features are available on every plan. The only difference is the monthly message limit.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <th className="text-left py-3 pr-4 font-semibold text-gray-700 dark:text-gray-300 w-44">Feature</th>
                      {PLANS.map(p => (
                        <th key={p.id} className={`text-center py-3 px-2 font-bold ${subscription?.plan_id === p.id ? 'text-emerald-600' : 'text-gray-900 dark:text-white'}`}>
                          {p.name}
                          {subscription?.plan_id === p.id && (
                            <span className="ml-1 text-[9px] bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded-full">Now</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Messages/mo',        values: ['200', '2,500', '7,500', '25,000', '10,000', 'Unlimited'] },
                      { label: 'WA Numbers',          values: ['1', '1', '1', '3', '1', 'Unlimited']                   },
                      { label: 'Auto-reply bot',      values: [true, true, true, true, true, true]                      },
                      { label: 'Unlimited keywords',  values: [true, true, true, true, true, true]                      },
                      { label: 'Products & FAQs',     values: [true, true, true, true, true, true]                      },
                      { label: 'Analytics',           values: [true, true, true, true, true, true]                      },
                      { label: 'Lead Capture',        values: [true, true, true, true, true, true]                      },
                      { label: 'Bulk Broadcast',      values: [true, true, true, true, true, true]                      },
                      { label: 'Multi-agent',         values: [false, false, false, true, false, true]                  },
                      { label: 'White Label',         values: [false, false, false, false, false, true]                 },
                      { label: 'Monthly Fee',         values: ['Free', '$19', '$49', '$149', '$499 once', 'Custom']     },
                    ].map(row => (
                      <tr key={row.label} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-400 font-medium">{row.label}</td>
                        {row.values.map((val, i) => (
                          <td key={i} className="text-center py-3 px-2">
                            {typeof val === 'boolean'
                              ? val
                                ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                                : <XCircle      className="w-4 h-4 text-gray-300 mx-auto" />
                              : <span className="text-gray-800 dark:text-gray-200 font-medium text-xs">{val}</span>
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Trust badges */}
            <motion.div {...fadeUp(0.48)}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { icon: Shield,       title: '7-Day Money Back',  desc: 'Not satisfied? Full refund within 7 days, no questions asked.',  color: 'text-emerald-600', bg: 'bg-emerald-50' },
                  { icon: Lock,         title: 'Secure Payments',   desc: '256-bit SSL encryption. PCI-DSS compliant via Razorpay.',         color: 'text-blue-600',   bg: 'bg-blue-50'    },
                  { icon: InfinityIcon, title: 'No Lock-in',        desc: 'Cancel anytime. No hidden fees. No cancellation charges.',        color: 'text-violet-600', bg: 'bg-violet-50'  },
                ].map(({ icon: Icon, title, desc, color, bg }) => (
                  <div key={title} className="flex items-start gap-4 p-5 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                    <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${color}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm">{title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* FAQ */}
            <motion.div {...fadeUp(0.52)}>
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
                <h2 className="text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-emerald-500" /> Billing FAQs
                </h2>
                <div className="space-y-2">
                  {FAQS.map((faq, i) => (
                    <FaqItem key={i} faq={faq} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? null : i)} />
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* ── ADD-ONS TAB ── */}
        {activeTab === 'addons' && (
          <motion.div key="addons" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-5">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Zap className="w-4 h-4 text-emerald-500" /> Available Add-ons
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5">Boost your plan with extra capacity</p>
                </div>
                {activeAddons.filter(id => ADD_ONS.find(a => a.id === id && a.price > 0)).length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Total: <span className="font-bold text-emerald-600">
                        +${activeAddons.reduce((s, id) => s + (ADD_ONS.find(a => a.id === id)?.price || 0), 0)}/mo
                      </span>
                    </span>
                    <Btn variant="primary" className="px-4 py-2 text-sm" onClick={handleSaveAddons}>
                      Save Changes
                    </Btn>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ADD_ONS.map(addon => (
                  <AddOnCard key={addon.id} addon={addon} active={activeAddons.includes(addon.id)} onToggle={handleToggleAddon} />
                ))}
              </div>
            </div>

            {/* Lifetime deal promo */}
            {!isCurrentLifetime && (
              <div className="relative bg-gradient-to-br from-yellow-400 via-orange-400 to-red-500 rounded-2xl p-6 md:p-8 overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
                <div className="relative flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-white text-xs font-bold mb-3">
                      <Flame className="w-3.5 h-3.5" />
                      {lifetimeSpotsLeft !== null ? `Only ${lifetimeSpotsLeft} spots left!` : 'Limited spots!'}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Lifetime Deal 🔥</h3>
                    <p className="text-white/90 text-sm mb-4">Pay once. Get 10,000 messages/month + all features forever. No monthly fees.</p>
                    <div className="flex items-baseline gap-3">
                      <span className="text-4xl font-bold text-white">$499</span>
                      <span className="text-xl line-through text-white/60">$1,176</span>
                      <span className="px-2 py-0.5 rounded-full bg-white/20 text-white text-xs font-bold">Save $677</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Btn
                      variant="ghost"
                      className="px-6 py-3 text-sm font-bold"
                      disabled={lifetimeSpotsLeft === 0}
                      onClick={() => handleSelectPlan(PLANS.find(p => p.id === 'lifetime'))}
                    >
                      {lifetimeSpotsLeft === 0 ? 'Sold Out' : 'Grab Lifetime Deal'} <ArrowRight className="w-4 h-4" />
                    </Btn>
                  </div>
                </div>
              </div>
            )}

            {isCurrentLifetime && (
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/10 dark:to-yellow-900/10 rounded-2xl border-2 border-orange-200 dark:border-orange-800 p-6 text-center">
                <div className="text-4xl mb-3">♾️</div>
                <h3 className="text-lg font-bold text-orange-700 dark:text-orange-300 mb-1">You're on the Lifetime Deal!</h3>
                <p className="text-sm text-orange-600 dark:text-orange-400">Enjoy 10,000 messages/month and all features forever — no monthly fees, ever.</p>
              </div>
            )}
          </motion.div>
        )}

        {/* ── BILLING TAB ── */}
        {activeTab === 'billing' && (
          <motion.div key="billing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-5">

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  label: 'Next Billing Date',
                  value: isCurrentLifetime ? '∞ Never' : subscription?.ends_at
                    ? new Date(subscription.ends_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                    : 'N/A',
                  icon: Calendar, color: 'text-blue-600', bg: 'bg-blue-50',
                },
                {
                  label: 'Amount Due',
                  value: isCurrentLifetime
                    ? '✅ Paid Once'
                    : subscription?.plan_id === 'starter'
                      ? 'Free'
                      : `$${yearly
                          ? PLANS.find(p => p.id === subscription?.plan_id)?.yearlyPrice
                          : PLANS.find(p => p.id === subscription?.plan_id)?.monthlyPrice
                        }/mo`,
                  icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50',
                },
                {
                  label: 'Total Paid',
                  value: `₹${invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0).toLocaleString()}`,
                  icon: Receipt, color: 'text-violet-600', bg: 'bg-violet-50',
                },
              ].map(({ label, value, icon: Icon, color, bg }) => (
                <div key={label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-5 h-5 ${color}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Payment Method */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-emerald-500" /> Payment Method
                </h2>
                <Btn variant="outline" className="px-4 py-2 text-xs">Update Card</Btn>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Managed by Razorpay</p>
                  <p className="text-xs text-gray-400">
                    {subscription?.payment_id
                      ? `Last payment ID: ${subscription.payment_id.slice(0, 18)}...`
                      : 'No payment on file'}
                  </p>
                </div>
                {subscription?.payment_id && (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Active</span>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                <Lock className="w-3.5 h-3.5" />
                Your payment info is encrypted and secure
              </div>
            </div>

            {/* Invoice History */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-emerald-500" /> Invoice History
                </h2>
                <Btn
                  variant="outline"
                  className="px-4 py-2 text-xs gap-1.5"
                  onClick={() => {
                    const csv = [
                      ['Date', 'Description', 'Amount', 'Status'],
                      ...invoices.map(i => [
                        new Date(i.date).toLocaleDateString('en-IN'),
                        i.description,
                        `₹${i.amount}`,
                        i.status,
                      ])
                    ].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
                    const a    = document.createElement('a');
                    a.href     = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                    a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(a.href);
                    toast.success('Invoices exported!');
                  }}
                >
                  <Download className="w-3.5 h-3.5" /> Export All
                </Btn>
              </div>
              {invoices.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Receipt className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No invoices yet</p>
                </div>
              ) : (
                <div>{invoices.map(inv => <InvoiceRow key={inv.id} invoice={inv} />)}</div>
              )}
            </div>

            {/* Danger Zone */}
            {!isCurrentLifetime && subscription?.plan_id !== 'starter' && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-red-200 dark:border-red-800/30 shadow-sm p-6">
                <h2 className="text-base font-bold text-red-600 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Danger Zone
                </h2>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Cancel Subscription</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      You'll revert to the free Starter plan (200 msgs/month) at the end of your billing period.
                      All your data and features remain intact.
                    </p>
                  </div>
                  <Btn variant="danger" className="px-4 py-2 text-sm flex-shrink-0" onClick={() => setShowCancel(true)}>
                    Cancel Plan
                  </Btn>
                </div>
              </div>
            )}
          </motion.div>
        )}

      </AnimatePresence>

      {/* BOTTOM HELP BANNER */}
      <motion.div {...fadeUp(0.6)}>
        <div className="relative bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 overflow-hidden">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, rgba(16,185,129,0.15) 0%, transparent 60%)' }} />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                <Crown className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-bold text-base">Need help choosing a plan?</p>
                <p className="text-gray-400 text-sm mt-0.5">Our team is available to help you find the right message limit for your business</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.location.href = 'mailto:hello@truechattr.com'}
                className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold transition-colors"
              >
                Talk to Sales
              </button>
              <button
                onClick={() => navigate('/app/conversations')}
                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors border border-white/20"
              >
                Live Chat
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* MODALS */}
      <ConfirmModal
        plan={confirmPlan}
        yearly={yearly}
        addons={activeAddons}
        hasPaidBefore={hasPaidBefore}
        onConfirm={handleConfirmSubscription}
        onClose={() => setConfirmPlan(null)}
        loading={planLoading === confirmPlan?.id}
      />

      {showCancel && (
        <CancelModal
          onConfirm={handleCancelSubscription}
          onClose={() => setShowCancel(false)}
          loading={cancelLoading}
        />
      )}
    </div>
  );
}
